import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'

import Button from '@eig-builder/core-ui/Button'
import Checkbox from '@eig-builder/core-ui/Checkbox'
import FormControlLabel from '@eig-builder/core-ui/FormControlLabel'
import InputAdornment from '@eig-builder/core-ui/InputAdornment'
import TextField from '@eig-builder/core-ui/TextField'
import Typography from '@eig-builder/core-ui/Typography'

import { getCurrencySymbol } from '@eig-builder/core-utils/helpers/currency-helper'
import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'

import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'
import { showError, showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'

import ButtonWithLoader from 'core/components/button-with-loader'
import Modal from 'core/components/modal'

import { UserHasRole, UserHasAnyRole } from '../../../helpers/role-management'
import { ROLES } from '../../../helpers/role-management/role-mapping.js'

import Text from '@eig-builder/module-localization'
import './lang'

const styles = theme => ({
  content: {
    paddingTop: theme.spacing(2)
  },
  fieldWrapper: {
    marginTop: theme.spacing(4)
  },
  errorText: {
    color: theme.palette.error.main
  },
  actions: {
    margin: `${theme.spacing(4)}px`
  }
})

class AddCredits extends React.Component {
  static contextType = NotificationBarContext

  state = {
    amount: '',
    amountError: '',
    ammountInvalid: false,
    description: '',
    descriptionError: '',
    sendCreditNote: false
  }

  componentDidUpdate (prevProps) {
    if (prevProps.posting && !this.props.posting && this.props.response && this.props.response.credit_id) {
      this.props.onClose()
      showSuccess('The credit has been added successfully.')(this.context.dispatch)
      this.props.getGoodwillCreditsForAccount(this.props.account.id, this.props.account.partnerId)
      this.resetState()
    }

    if (prevProps.posting && !this.props.posting && this.props.response && this.props.response.error_message) {
      showError(this.props.response.error_message || 'Something went wrong.')(this.context.dispatch)
    }
  }

  userHasValidRolesForCredits = (amount) => {
    let ammountValid = false

    if (amount <= 50) {
      ammountValid = true
    } else if (UserHasRole(ROLES.SUPPORT) && amount <= 75) {
      ammountValid = true
    } else if (UserHasRole(ROLES.ESCALATED_SUPPORT) && amount <= 150) {
      ammountValid = true
    } else if (UserHasAnyRole([ROLES.SUPPORT_MANAGER, ROLES.ADMIN_CST, ROLES.ADMIN_MP])) {
      ammountValid = true
    }
    return ammountValid
  }

  handleAmountChange = event => {
    const amount = event.target.value
    const ammountInvalid = !this.userHasValidRolesForCredits(amount)

    this.setState({
      amount,
      ammountInvalid,
      amountError: ''
    })
  }

  handleDescriptionChange = event => {
    this.setState({
      description: event.target.value,
      descriptionError: ''
    })
  }

  handleCheckbox = event => {
    this.setState({
      sendCreditNote: event.target.checked
    })
  }

  setError = (field, error) => {
    this.setState({
      [`${field}Error`]: error
    })
  }

  resetState = () => {
    this.setState({
      amount: '',
      amountError: '',
      ammountInvalid: false,
      description: '',
      descriptionError: '',
      sendCreditNote: false
    })
  }

  onSubmit = () => {
    let valid = true
    if (!this.state.amount || this.state.amount <= 0) {
      this.setError('amount', 'Please enter a valid amount!')
      valid = false
    }
    if (!this.state.description) {
      this.setError('description', 'Please enter a description!')
      valid = false
    }

    if (valid) {
      const { account } = this.props
      const data = {
        uber_client_id: account.uberClientId,
        amount: this.state.amount,
        description: this.state.description,
        send_note: this.state.sendCreditNote
      }

      this.props.addCredits(data, account.id, account.partnerId)
    }
  }

  render () {
    const { classes, open, posting, account } = this.props

    const actions = [
      <Button
        key='add-credits-cancel'
        color='secondary'
        disabled={posting}
        onClick={this.props.onClose}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-account-details-add-credits-cancel'
        dataElementId='cst-account-details-add-credits-cancel'
      >
        <Text message='modules.crm.components.account.addCredits.cancel' />
      </Button>,
      <ButtonWithLoader
        key='add-credits-submit'
        disabled={this.state.ammountInvalid || posting}
        onClick={this.onSubmit}
        dataElementSuffix='account-details--add-credits-add'
        showLoader={!!posting}
        text={<Text message='modules.crm.components.account.addCredits.confirm' />}
      />
    ]

    return (
      <Modal
        actions={actions}
        dataElementPrefix='cst-account--add-credits'
        disableBackdropClick
        handleClose={this.props.onClose}
        open={open}
        title={<Text message='modules.crm.components.account.addCredits.title' />}
      >
        <div className='row align-items-center'>
          <div className='col-4'>
            <TextField
              label={<Text message='modules.crm.components.account.addCredits.labels.amount' />}
              type='number'
              error={!!this.state.amountError}
              helperText={this.state.amountError}
              value={this.state.amount}
              onChange={this.handleAmountChange}
              variant='outlined'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{getCurrencySymbol(account.currencyCode)}</InputAdornment>
                )
              }}
              dataElementLocation={DataElementLocations.BODY}
              dataElementLabel='cst-account-add-credits-amount'
              dataElementId='cst-account-add-credits-amount'
            />
          </div>
          <div className='col'>
            {this.state.ammountInvalid && (
              <Typography className={classes.errorText} variant='body2'>
                <Text message='modules.crm.components.account.addCredits.amountError' />
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.fieldWrapper}>
          <TextField
            fullWidth
            label={<Text message='modules.crm.components.account.addCredits.labels.description' />}
            placeholder='Enter a description...'
            value={this.state.description}
            error={!!this.state.descriptionError}
            helperText={this.state.descriptionError}
            onChange={this.handleDescriptionChange}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-add-credits-description'
            dataElementId='cst-account-add-credits-description'
          />
        </div>
        <div className={classes.fieldWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.sendCreditNote || false}
                onChange={this.handleCheckbox}
                value='sendCreditNote'
                color='primary'
                dataElementLocation={DataElementLocations.BODY}
                dataElementLabel='cst-account--add-credits--send-note-checkbox'
                dataElementId='cst-account--add-credits--send-note-checkbox'
              />
            }
            label={<Text message='modules.crm.components.account.addCredits.labels.sendNote' />}
          />
        </div>
      </Modal>
    )
  }
}

AddCredits.propTypes = {
  account: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addCredits: PropTypes.func.isRequired,
  posting: PropTypes.bool,
  response: PropTypes.object,
  getGoodwillCreditsForAccount: PropTypes.func.isRequired
}

export default withStyles(styles)(AddCredits)
